import { InputNumber, InputNumberProps } from "@gw/web-basic-components";
import React, { useEffect, useRef, useState } from "react";
import { round, truncate } from "./utils";
import { isNullOrUndefined } from "../utils";

export interface NumberInputProps extends Omit<InputNumberProps, "formatter"> {
  // 是否在blur时出发change事件
  changeOnBlur?: boolean;
  // 舍入规则
  rounding?: "round" | "truncate";
  // 精度 即小数位数
  precision?: number;
  // 是否补尾零
  trailingZeroes?: boolean;
  // 千位分隔符
  thousandSeparator?: boolean;
}

// 格式化值
const parseValue = (
  value: number | string,
  rounding: "round" | "truncate",
  precision: number,
  trailingZeroes: boolean,
  thousandSeparator: boolean
) => {
  const num = Number(value);
  if (rounding === "round") {
    return round(num, precision, trailingZeroes, thousandSeparator);
  }
  if (rounding === "truncate") {
    return truncate(num, precision, trailingZeroes, thousandSeparator);
  }
  return num.toString();
};

// 计算数字输入框宽度
const calculateStringWidth = (str: string) => {
  let width = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (/[a-z0-9]/.test(char)) {
      // 如果是数字&小写字母
      width += 10;
    } else if ([".", ","].includes(char)) {
      // 如果是点
      width += 2;
    } else {
      // 其他字符
      width += 12;
    }
  }
  return width;
};

export default function NumberInput(props: NumberInputProps) {
  const {
    value,
    onFocus,
    onBlur,
    onChange,
    changeOnBlur = true,
    style,
    precision = 4,
    rounding = "round",
    trailingZeroes = false,
    thousandSeparator = false,
    ...rest
  } = props;
  const [focus, setFocus] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  // 输入框宽度
  const [width, setWidth] = useState(0);
  // 输入框原始宽度
  const originWidthRef = useRef(0);
  // 真实值
  const realValue = useRef(value);
  // 显示值
  const showValue =
    focus || isNullOrUndefined(value)
      ? value
      : parseValue(
          value,
          rounding,
          precision,
          trailingZeroes,
          thousandSeparator
        );
  // 设置显示宽度
  const setShowWidth = (v: number | string) => {
    const w = calculateStringWidth(v?.toString?.() ?? "");
    setWidth(w > originWidthRef.current ? w : 0);
  };

  useEffect(() => {
    realValue.current = value;
  }, [value]);

  useEffect(() => {
    originWidthRef.current = ref.current?.offsetWidth || 40;
  }, []);

  useEffect(() => {
    if (isNullOrUndefined(showValue)) {
      setWidth(0);
    } else {
      setShowWidth(showValue.toString());
    }
  }, [showValue]);

  return (
    <InputNumber
      {...rest}
      ref={ref}
      value={showValue}
      style={
        width === 0
          ? style
          : {
              width,
              ...style,
            }
      }
      onChange={(v) => {
        realValue.current = v;
        onChange?.(v);
      }}
      onInput={(v) => {
        setShowWidth(v);
      }}
      onFocus={(...args) => {
        setFocus(true);
        onFocus?.(...args);
      }}
      onBlur={(...args) => {
        if (changeOnBlur) {
          onChange?.(realValue.current);
        }
        setFocus(false);
        onBlur?.(...args);
      }}
      controls={false}
    />
  );
}
